import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProfileNavigation from '../components/ProfileNavigation';
import EventCard from '../components/EventCard';
import { IconBookmark, IconStar, IconMusic, IconLogin } from '@tabler/icons-react';
import { ROUTES } from '../constants/routes';
import styles from './UserProfilePage.module.css';
import { userService, eventService } from '../services/api';
import { Event } from '../types/event';

const UserProfilePage: React.FC = () => {
  const [savedEvents, setSavedEvents] = useState<Event[]>([]);
  const [interestedEvents, setInterestedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Vérifier d'abord si l'utilisateur est connecté
  useEffect(() => {
    async function checkAuthentication() {
      try {
        await userService.getCurrentUser();
        setAuthenticated(true);
      } catch (err) {
        console.error('Erreur d\'authentification:', err);
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    }
    
    checkAuthentication();
  }, []);

  // Récupérer les événements uniquement si l'utilisateur est authentifié
  useEffect(() => {
    if (authenticated) {
      fetchEvents();
    }
  }, [authenticated]);

  async function fetchEvents() {
    try {
      setLoading(true);
      const [saved, interested] = await Promise.all([
        userService.getSavedEvents().catch(err => {
          console.error('Erreur lors de la récupération des événements sauvegardés:', err);
          return [];
        }),
        userService.getInterestedEvents().catch(err => {
          console.error('Erreur lors de la récupération des événements intéressants:', err);
          return [];
        })
      ]);
      
      setSavedEvents(saved);
      setInterestedEvents(interested);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération des événements:', err);
      setError('Impossible de récupérer vos événements.');
    } finally {
      setLoading(false);
    }
  }

  // Fonctions pour gérer les interactions avec les événements
  const handleInterestClick = async (eventId: number) => {
    try {
      await eventService.toggleInterest(eventId);
      // Mettre à jour l'état local pour refléter le changement
      // Pour les événements intéressés: les supprimer de la liste si désintéressé
      setInterestedEvents(prev => prev.filter(event => event.id !== eventId));
      
      // Pour les événements sauvegardés: simplement mettre à jour le statut isInterested
      setSavedEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { ...event, isInterested: !event.isInterested } 
          : event
      ));
      
      // Actualiser les données après une courte période
      setTimeout(() => fetchEvents(), 500);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'intérêt:', err);
      setError('Une erreur est survenue lors de la mise à jour de votre intérêt.');
    }
  };

  const handleSaveClick = async (eventId: number) => {
    try {
      await eventService.toggleSave(eventId);
      
      // Mettre à jour l'état local pour refléter le changement
      // Pour les événements sauvegardés: les supprimer de la liste si désauvegardé
      setSavedEvents(prev => prev.filter(event => event.id !== eventId));
      
      // Pour les événements intéressés: simplement mettre à jour le statut isSaved
      setInterestedEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { ...event, isSaved: !event.isSaved } 
          : event
      ));
      
      // Actualiser les données après une courte période
      setTimeout(() => fetchEvents(), 500);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde/désauvegarde de l\'événement:', err);
      setError('Une erreur est survenue lors de la sauvegarde/désauvegarde de l\'événement.');
    }
  };

  // Afficher un message si l'utilisateur n'est pas connecté
  if (authenticated === false) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>Mon profil</h1>
        
        <div className={styles.notAuthenticatedContainer}>
          <div className={styles.notAuthenticatedContent}>
            <IconLogin size={48} className={styles.notAuthenticatedIcon} />
            <h2>Connexion requise</h2>
            <p>Vous devez être connecté pour accéder à votre profil et voir vos événements.</p>
            <div className={styles.notAuthenticatedActions}>
              <Link to={ROUTES.HOME} className={styles.loginButton}>
                Se connecter
              </Link>
              <Link to={ROUTES.HOME} className={styles.registerButton}>
                Créer un compte
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Mon profil</h1>
      <p className={styles.subtitle}>
        Bienvenue sur votre espace personnel. Retrouvez vos événements et gérez votre profil.
      </p>
      
      <ProfileNavigation />
      
      {/* Section des événements sauvegardés */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <IconBookmark size={24} />
            Événements sauvegardés
          </h2>
          <Link to={ROUTES.SAVED_EVENTS} className={styles.sectionLink}>
            Voir tous
          </Link>
        </div>
        
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Chargement de vos événements...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>{error}</p>
          </div>
        ) : savedEvents.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Vous n'avez pas encore sauvegardé d'événements.</p>
          </div>
        ) : (
          <div className={styles.eventsGrid}>
            {savedEvents.slice(0, 3).map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                onInterestClick={handleInterestClick}
                onSaveClick={handleSaveClick}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Section des événements intéressants */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <IconStar size={24} />
            Événements qui m'intéressent
          </h2>
          <Link to={ROUTES.INTERESTED_EVENTS} className={styles.sectionLink}>
            Voir tous
          </Link>
        </div>
        
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Chargement de vos événements...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>{error}</p>
          </div>
        ) : interestedEvents.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Vous n'avez pas encore marqué d'événements comme intéressants.</p>
          </div>
        ) : (
          <div className={styles.eventsGrid}>
            {interestedEvents.slice(0, 3).map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                onInterestClick={handleInterestClick}
                onSaveClick={handleSaveClick}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Information sur la page profil */}
      <div className={styles.infoPanel}>
        <h3>Gérer mon profil</h3>
        <p>
          Vous pouvez consulter et modifier vos informations personnelles dans l'onglet 
          <Link to={ROUTES.PERSONAL_INFO} className={styles.infoLink}> Informations personnelles</Link>.
        </p>
      </div>
      
      {/* Section du profil staff */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <IconMusic size={24} />
            Profil Staff
          </h2>
          <Link to={ROUTES.STAFF_PROFILE} className={styles.sectionLink}>
            Gérer
          </Link>
        </div>
        
        <div className={styles.staffSection}>
          <p>
            En tant que staff, vous pouvez aider à l'organisation d'événements et postuler à des offres.
          </p>
          <Link to={ROUTES.STAFF_PROFILE} className={styles.staffProfileButton}>
            <IconMusic size={18} />
            Accéder à mon profil staff
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
